import type {
	FlightSearchCriteria,
	FlightOffer,
	FlightAirline,
	FlightBaggageOption,
	FlightConditionOption,
	FlightBaggageSummary,
	FlightConditionsSummary,
	FlightItinerary,
	FlightSegment,
	FlightPlace,
} from '../types/flight';

export const SESSION_TOKEN_KEY = 'noetl_gateway_example_session_token';

export const resolveGatewayBaseUrl = () => {
	const envUrl = (process.env.REACT_APP_GATEWAY_URL || '').trim();
	if (envUrl) return envUrl.replace(/\/+$/, '');

	if (typeof window === 'undefined') return '';

	const { origin, hostname, protocol } = window.location;
	if (hostname === 'localhost' || hostname === '127.0.0.1') {
		// Use relative paths so the CRA proxy (https://gateway.mestumre.dev) handles routing
		return '';
	}

	if (hostname === 'gateway.mestumre.dev' || hostname.startsWith('gateway.')) {
		return origin.replace(/\/+$/, '');
	}

	const stripped = hostname.replace(/^(www\.|gui\.)/i, '');
	const labels = stripped.split('.');
	const base = labels.length >= 3 ? labels.slice(1).join('.') : stripped;
	return `${protocol}//gateway.${base}`;
};

export const loginToGateway = async (idToken: string, auth0Domain?: string): Promise<string> => {
	const response = await fetch(`${resolveGatewayBaseUrl()}/api/auth/login`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			auth0_token: idToken,
			auth0_domain: auth0Domain,
		}),
	});

	const body = await response.json().catch(() => ({}));
	if (!response.ok) {
		throw new Error(body?.error || body?.message || `Gateway auth failed (${response.status})`);
	}

	const sessionToken = body?.session_token || body?.sessionToken || '';
	if (!sessionToken) {
		throw new Error('Gateway did not return session_token.');
	}

	localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
	return sessionToken;
};

const fetchExecution = async (path: string, token: string, signal?: AbortSignal): Promise<Response> => {
	return fetch(path, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		signal,
	});
};

export const getExecution = async (executionId: string, tokenOverride?: string, signal?: AbortSignal): Promise<Record<string, unknown>> => {
	const token = (tokenOverride || localStorage.getItem(SESSION_TOKEN_KEY) || '').trim();
	if (!token) {
		throw new Error('Missing gateway session token. Please log in again.');
	}

	const base = resolveGatewayBaseUrl();
	const encodedId = encodeURIComponent(executionId);
	// `/api` is the direct NoETL API namespace. Through the gateway, execution
	// routes are exposed under `/noetl`; do not retry a gateway 404 against the
	// backend-only namespace because that creates a second, misleading 404.
	const response = await fetchExecution(`${base}/noetl/executions/${encodedId}`, token, signal);
	const body = await response.json().catch(() => ({}));
	if (!response.ok) {
		const detail = (body as any)?.error || (body as any)?.message || `Execution fetch failed (${response.status})`;
		throw new Error(`${String(detail)} (${response.status})`);
	}
	return body as Record<string, unknown>;
	/*
	 * Keep the execution read path simple. The endpoint is fixed and now returns
	 * the final payload shape directly without pagination stitching.
	 */
};

const ITINERARY_PLAYBOOK_PATH = 'muno/playbooks/itinerary-planner';
const HOTEL_CARDS_PLAYBOOK_PATH = 'muno/playbooks/hotel-cards';
const FLIGHTS_DETAILS_PLAYBOOK_PATH = 'muno/playbooks/flights-details';

const abortableSleep = (ms: number, signal?: AbortSignal) =>
	new Promise<void>((resolve, reject) => {
		if (signal?.aborted) {
			reject(new DOMException('Aborted', 'AbortError'));
			return;
		}
		const timeout = window.setTimeout(() => {
			signal?.removeEventListener('abort', onAbort);
			resolve();
		}, ms);
		const onAbort = () => {
			window.clearTimeout(timeout);
			reject(new DOMException('Aborted', 'AbortError'));
		};
		signal?.addEventListener('abort', onAbort, { once: true });
	});
const isRecord = (value: unknown): value is Record<string, unknown> =>
	Boolean(value && typeof value === 'object' && !Array.isArray(value));

const TERMINAL_STATUSES = new Set([
	'completed',
	'succeeded',
	'failed',
	'error',
	'cancelled',
	'canceled',
]);

const extractExecutionStatus = (execution: Record<string, unknown>): string => {
	const data = isRecord(execution.data) ? (execution.data as Record<string, unknown>) : undefined;
	const result = isRecord(execution.result) ? (execution.result as Record<string, unknown>) : undefined;
	return String(execution.status || data?.status || result?.status || '').trim().toLowerCase();
};

export type ItineraryExecutionSnapshot = {
	topLevelKeys: string[];
	status: string;
	resultKeys: string[];
	dataKeys: string[];
	eventsCount: number;
	lastEventKeys: string[];
	lastEventType: unknown;
	lastEventNode: unknown;
	resultPreview: unknown;
	dataPreview: unknown;
	lastEventPreview: unknown;
};

export const inspectExecutionResult = (executionResult: Record<string, unknown>): ItineraryExecutionSnapshot => {
	const events = Array.isArray(executionResult.events) ? (executionResult.events as Record<string, unknown>[]) : [];
	const lastEvent = events.length ? events[events.length - 1] : undefined;
	const result = (executionResult.result || {}) as Record<string, unknown>;
	const data = (executionResult.data || {}) as Record<string, unknown>;

	return {
		topLevelKeys: Object.keys(executionResult),
		status: String(executionResult.status || ''),
		resultKeys: Object.keys(result),
		dataKeys: Object.keys(data),
		eventsCount: events.length,
		lastEventKeys: lastEvent ? Object.keys(lastEvent) : [],
		lastEventType: lastEvent?.event_type,
		lastEventNode: lastEvent?.node_name,
		resultPreview: executionResult.result || null,
		dataPreview: executionResult.data || null,
		lastEventPreview: lastEvent || null,
	};
};

export const waitForExecutionResult = async (executionId: string, token: string, signal?: AbortSignal): Promise<Record<string, unknown>> => {
	const maxAttempts = 120;
	for (let i = 0; i < maxAttempts; i += 1) {
		if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
		let execution: Record<string, unknown>;
		try {
			execution = await getExecution(executionId, token, signal);
		} catch (error) {
			// The execution document can briefly be absent from the gateway's
			// state projection after dispatch. Keep polling instead of failing on
			// the first transient 404.
			if (!(error instanceof Error) || !/\(404\)/.test(error.message)) throw error;
			await abortableSleep(1500, signal);
			continue;
		}
		const status = extractExecutionStatus(execution);
		if (TERMINAL_STATUSES.has(status)) {
			return execution;
		}
		await abortableSleep(1500, signal);
		if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
	}

	// One final read before timing out to avoid false negatives on slow backends.
	const finalExecution = await getExecution(executionId, token, signal);
	const finalStatus = extractExecutionStatus(finalExecution);
	if (TERMINAL_STATUSES.has(finalStatus)) {
		return finalExecution;
	}
	throw new Error(`Execution ${executionId} did not complete in time`);
};

export const cancelExecution = async (executionId: string): Promise<void> => {
	const token = (localStorage.getItem(SESSION_TOKEN_KEY) || '').trim();
	if (!token) return;
	const base = resolveGatewayBaseUrl();
	const encodedId = encodeURIComponent(executionId);
	await fetch(`${base}/noetl/executions/${encodedId}/cancel`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ reason: 'Cancelled from Adiona UI', cascade: true }),
	}).catch(() => undefined);
};

export type ItineraryPlaybookResult = {
	executionId: string;
	status: string;
	startResponse: Record<string, unknown>;
	snapshot: ItineraryExecutionSnapshot;
	extracted: {
		envelope: Record<string, unknown> | null;
		slotState: Record<string, unknown> | null;
		botMessage: string;
		source: string;
	};
	executionResult: Record<string, unknown>;
};

export type ItineraryTurnInput = {
	threadId?: string;
	eventType?: 'user_message' | 'user_widget_submit' | 'user_widget_cta_click';
	eventPayload?: Record<string, unknown>;
	userMessage?: string;
};

export type HotelCardsPlaybookInput = {
	city?: string;
	latitude?: number;
	longitude?: number;
	radius?: number;
	check_in?: string;
	check_out?: string;
	adults?: number;
	rooms?: number;
	children?: number;
	children_ages?: number[];
	limit?: number;
	max_rooms?: number;
	price_bands?: number;
};

export type HotelCardsPlaybookResult = {
	executionId: string;
	status: string;
	startResponse: Record<string, unknown>;
	hotels: Record<string, unknown>[];
	count: number;
	nights: number;
	currency?: string;
	check_in?: string;
	check_out?: string;
	source?: string;
	hotelbeds_env?: string;
	provider_error?: unknown;
	executionResult: Record<string, unknown>;
};

export type FlightDetailsPlaybookInput = {
	tripType: 'one_way' | 'round_trip';
	origin: string;
	destination: string;
	departDate: string;
	returnDate?: string;
	travelers: { adults: number; children: { age: number }[] };
};

export type FlightDetailsPlaybookResult = {
	executionId: string;
	status: string;
	startResponse: Record<string, unknown>;
	offers: Record<string, unknown>[];
	count: number;
	currency?: string;
	trip_type?: string;
	provider_error?: unknown;
	executionResult: Record<string, unknown>;
};

const extractHotelCardsPayload = (executionResult: Record<string, unknown>): Record<string, unknown> | null => {
	const result = isRecord(executionResult.result) ? (executionResult.result as Record<string, unknown>) : {};
	const data = isRecord(executionResult.data) ? (executionResult.data as Record<string, unknown>) : {};
	const events = Array.isArray(executionResult.events) ? (executionResult.events as Record<string, unknown>[]) : [];

	const hasHotelsArray = (value: unknown): value is Record<string, unknown> & { hotels: unknown[] } =>
		isRecord(value) && Array.isArray((value as Record<string, unknown>).hotels);

	const directCandidates: unknown[] = [
		result,
		data,
		isRecord(result.result) ? result.result : null,
		isRecord(result.data) ? result.data : null,
		isRecord(data.result) ? data.result : null,
		isRecord(data.data) ? data.data : null,
	];

	for (const candidate of directCandidates) {
		if (hasHotelsArray(candidate)) return candidate;
	}

	for (let i = events.length - 1; i >= 0; i -= 1) {
		const event = events[i];
		if (!isRecord(event)) continue;
		for (const node of [event.output_result, event.result]) {
			if (!isRecord(node)) continue;
			const context = isRecord((node as any).context)
				? ((node as any).context as Record<string, unknown>)
				: (node as Record<string, unknown>);
			if (hasHotelsArray(context)) return context;
			if (hasHotelsArray(context.result)) return context.result as Record<string, unknown>;
			if (hasHotelsArray(context.data)) return context.data as Record<string, unknown>;
		}
	}

	// Final fallback: deep scan all execution branches for the first object
	// carrying hotels[] to tolerate runtime-specific wrapping.
	const visited = new Set<unknown>();
	const findHotelsDeep = (value: unknown, depth = 0): Record<string, unknown> | null => {
		if (depth > 9 || value === null || value === undefined || visited.has(value)) return null;
		if (typeof value === 'object') visited.add(value);
		if (hasHotelsArray(value)) return value as Record<string, unknown>;
		if (Array.isArray(value)) {
			for (let i = value.length - 1; i >= 0; i -= 1) {
				const found = findHotelsDeep(value[i], depth + 1);
				if (found) return found;
			}
			return null;
		}
		if (!isRecord(value)) return null;
		for (const nested of Object.values(value)) {
			const found = findHotelsDeep(nested, depth + 1);
			if (found) return found;
		}
		return null;
	};

	return findHotelsDeep(executionResult);
};

const extractFlightDetailsPayload = (executionResult: Record<string, unknown>): Record<string, unknown> => {
	const visited = new Set<unknown>();
	const findOffers = (value: unknown, depth = 0): Record<string, unknown> | null => {
		if (depth > 10 || value === null || value === undefined || visited.has(value)) return null;
		if (typeof value === 'object') visited.add(value);
		if (isRecord(value) && Array.isArray(value.offers)) return value;
		if (Array.isArray(value)) {
			for (let i = value.length - 1; i >= 0; i -= 1) {
				const found = findOffers(value[i], depth + 1);
				if (found) return found;
			}
			return null;
		}
		if (!isRecord(value)) return null;
		for (const nested of Object.values(value)) {
			const found = findOffers(nested, depth + 1);
			if (found) return found;
		}
		return null;
	};

	return findOffers(executionResult) || {};
};

export const executeFlightDetailsPlaybook = async (
	token: string,
	input: FlightDetailsPlaybookInput,
	options?: { signal?: AbortSignal; onExecutionId?: (id: string) => void }
): Promise<FlightDetailsPlaybookResult> => {
	const mutation = `
		mutation ExecutePlaybook($name: String!, $vars: JSON) {
			executePlaybook(name: $name, variables: $vars) {
				id
				executionId
				requestId
				name
				status
			}
		}
	`;
	const response = await fetch(`${resolveGatewayBaseUrl()}/graphql`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({
			query: mutation,
			variables: {
				name: FLIGHTS_DETAILS_PLAYBOOK_PATH,
				vars: {
					trip_type: input.tripType,
					origin: input.origin,
					destination: input.destination,
					depart_date: input.departDate,
					return_date: input.returnDate || '',
					adults: input.travelers.adults,
					children: input.travelers.children,
					criteria: input,
				},
			},
		}),
		signal: options?.signal,
	});

	if (response.status === 401) throw new Error('Session expired. Please try again.');
	if (!response.ok) throw new Error(`Flight details request failed (${response.status})`);
	const body = await response.json();
	if (body.errors?.length) throw new Error(body.errors[0].message || 'GraphQL error');

	const execution = body.data?.executePlaybook || {};
	const executionId = String(execution.executionId || execution.execution_id || execution.id || '').trim();
	if (!executionId) throw new Error('Missing execution ID from flights-details playbook response.');
	options?.onExecutionId?.(executionId);
	const executionResult = await waitForExecutionResult(executionId, token, options?.signal);
	const payload = extractFlightDetailsPayload(executionResult);
	const offers = Array.isArray(payload.offers) ? payload.offers.filter(isRecord) : [];

	return {
		executionId,
		status: String(executionResult.status || ''),
		startResponse: execution as Record<string, unknown>,
		offers,
		count: Number(payload.count || offers.length) || offers.length,
		currency: payload.currency ? String(payload.currency) : undefined,
		trip_type: payload.trip_type ? String(payload.trip_type) : undefined,
		provider_error: payload.provider_error,
		executionResult,
	};
};

const normalizeHotelCard = (raw: Record<string, unknown>, index: number): Record<string, unknown> => {
	const nights = Number(raw.nights || 0) || 0;
	const pricePerNight = Number(raw.pricePerNight || 0) || 0;
	const totalPrice = Number(raw.totalPrice || 0) || 0;
	const stars = Number(raw.stars || 0) || 0;
	const rooms = Array.isArray(raw.rooms) ? raw.rooms : [];
	const amenities = Array.isArray(raw.amenities) ? raw.amenities : [];

	return {
		id: String(raw.id || `hotel-${index + 1}`),
		name: String(raw.name || 'Hotel'),
		stars,
		bedType: String(raw.bedType || ''),
		price: String(raw.price || (pricePerNight ? `${raw.currency || ''} ${Math.round(pricePerNight)}`.trim() : '')),
		currency: String(raw.currency || ''),
		breakfastIncluded: Boolean(raw.breakfastIncluded),
		image: String(raw.image || ''),
		images: Array.isArray(raw.images) ? raw.images : [],
		__renderDetailCard: true,
		location: String(raw.location || ''),
		rating: Number(raw.rating || 0) || 0,
		ratingLabel: String(raw.ratingLabel || ''),
		pricePerNight,
		totalPrice,
		nights,
		amenities,
		description: String(raw.description || ''),
		category: String(raw.category || ''),
		rooms,
		roomsTotal: Number(raw.roomsTotal || rooms.length) || rooms.length,
		priceBands: Array.isArray(raw.priceBands) ? raw.priceBands : [],
	};
};

export const executeHotelCardsPlaybook = async (
	token: string,
	input: HotelCardsPlaybookInput = {},
	options?: { signal?: AbortSignal; onExecutionId?: (id: string) => void }
): Promise<HotelCardsPlaybookResult> => {
	const mutation = `
		mutation ExecutePlaybook($name: String!, $vars: JSON) {
			executePlaybook(name: $name, variables: $vars) {
				id
				executionId
				requestId
				name
				status
			}
		}
	`;

	// Send a full workload payload to avoid backend default-merging differences.
	const variables = {
		city: input.city || 'Monterey, California',
		latitude: input.latitude ?? 36.6002,
		longitude: input.longitude ?? -121.8947,
		radius: input.radius ?? 20,
		adults: input.adults ?? 2,
		rooms: input.rooms ?? 1,
		children: input.children ?? 0,
		limit: input.limit ?? 20,
		max_rooms: input.max_rooms ?? 10,
		price_bands: input.price_bands ?? 4,
		...(input.check_in ? { check_in: input.check_in } : {}),
		...(input.check_out ? { check_out: input.check_out } : {}),
		...(input.children_ages ? { children_ages: input.children_ages } : {}),
	};

	const response = await fetch(`${resolveGatewayBaseUrl()}/graphql`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify({
			query: mutation,
			variables: {
				name: HOTEL_CARDS_PLAYBOOK_PATH,
				vars: variables,
			},
		}),
		signal: options?.signal,
	});

	if (response.status === 401) {
		throw new Error('Session expired. Please try again.');
	}
	if (!response.ok) {
		throw new Error(`Hotel cards request failed (${response.status})`);
	}

	const body = await response.json();
	if (body.errors?.length) {
		throw new Error(body.errors[0].message || 'GraphQL error');
	}

	const execution = body.data?.executePlaybook || {};
	const executionId = String(execution.executionId || execution.execution_id || execution.id || '').trim();
	if (!executionId) {
		throw new Error('Missing execution ID from hotel-cards playbook response.');
	}

	options?.onExecutionId?.(executionId);
	const executionResult = await waitForExecutionResult(executionId, token, options?.signal);
	const payload = extractHotelCardsPayload(executionResult) || {};
	const rawHotels = Array.isArray(payload.hotels) ? (payload.hotels as Record<string, unknown>[]) : [];
	const hotels = rawHotels.map((hotel, index) => normalizeHotelCard(hotel, index));

	if (process.env.NODE_ENV !== 'production') {
		console.log('[hotel-cards] extraction', {
			executionId,
			status: String((executionResult as Record<string, unknown>)?.status || ''),
			hotelsCount: hotels.length,
			payloadKeys: Object.keys(payload),
			topLevelKeys: Object.keys(executionResult),
			resultKeys: isRecord((executionResult as Record<string, unknown>).result)
				? Object.keys((executionResult as Record<string, unknown>).result as Record<string, unknown>)
				: [],
			dataKeys: isRecord((executionResult as Record<string, unknown>).data)
				? Object.keys((executionResult as Record<string, unknown>).data as Record<string, unknown>)
				: [],
			providerError: payload.provider_error || null,
			requestVariables: variables,
		});
	}

	return {
		executionId,
		status: String((executionResult as Record<string, unknown>)?.status || ''),
		startResponse: execution as Record<string, unknown>,
		hotels,
		count: Number(payload.count || hotels.length) || hotels.length,
		nights: Number(payload.nights || 0) || 0,
		currency: payload.currency ? String(payload.currency) : undefined,
		check_in: payload.check_in ? String(payload.check_in) : undefined,
		check_out: payload.check_out ? String(payload.check_out) : undefined,
		source: payload.source ? String(payload.source) : undefined,
		hotelbeds_env: payload.hotelbeds_env ? String(payload.hotelbeds_env) : undefined,
		provider_error: payload.provider_error,
		executionResult,
	};
};

const extractItineraryData = (executionResult: Record<string, unknown>) => {
	const result = isRecord(executionResult.result) ? (executionResult.result as Record<string, unknown>) : {};
	const data = isRecord(executionResult.data) ? (executionResult.data as Record<string, unknown>) : {};
	const events = Array.isArray(executionResult.events) ? (executionResult.events as Record<string, unknown>[]) : [];

	// A valid widget envelope must have widget_type (string) + payload (object).
	// The playbook emits: { schema_version: 1, widget_type: "...", variant: "...", payload: {...} }
	const hasEnvelopePayload = (value: unknown): value is Record<string, unknown> =>
		isRecord(value) &&
		typeof (value as Record<string, unknown>).widget_type === 'string' &&
		String((value as Record<string, unknown>).widget_type).trim().length > 0 &&
		isRecord((value as Record<string, unknown>).payload);

	let envelope: Record<string, unknown> | null = null;
	let slotState: Record<string, unknown> | null = null;
	let botMessage = '';
	let source = '';

	// ── Primary: final_result step places widget at result.render ──
	// Also checks result.first_widget as a secondary key emitted by render_widget_chat.
	for (const ctx of [result, data]) {
		if (!isRecord(ctx) || Object.keys(ctx).length === 0) continue;
		if (!envelope) {
			if (hasEnvelopePayload(ctx.render)) {
				envelope = ctx.render as Record<string, unknown>;
				source = 'result.render';
			} else if (hasEnvelopePayload(ctx.first_widget)) {
				envelope = ctx.first_widget as Record<string, unknown>;
				source = 'result.first_widget';
			}
		}
		if (!slotState) {
			if (isRecord(ctx.final_slot_state)) slotState = ctx.final_slot_state as Record<string, unknown>;
			else if (isRecord(ctx.slot_state)) slotState = ctx.slot_state as Record<string, unknown>;
		}
		if (!botMessage) botMessage = String(ctx.bot_message || ctx.text || '').trim();
		if (envelope && slotState && botMessage) break;
	}

	// ── Secondary: scan execution step events for context carrying render/slot ──
	// noetl engine emits workflow.step.completed events; context lives in
	// event.output_result.context or event.result.context.
	if (!envelope || !slotState || !botMessage) {
		for (let i = events.length - 1; i >= 0; i -= 1) {
			const event = events[i];
			if (!isRecord(event)) continue;
			for (const node of [event.output_result, event.result]) {
				if (!isRecord(node)) continue;
				const ctx: Record<string, unknown> = isRecord((node as any).context)
					? ((node as any).context as Record<string, unknown>)
					: (node as Record<string, unknown>);
				if (!envelope) {
					if (hasEnvelopePayload(ctx.render)) { envelope = ctx.render as Record<string, unknown>; source = 'event.context.render'; }
					else if (hasEnvelopePayload(ctx.first_widget)) { envelope = ctx.first_widget as Record<string, unknown>; source = 'event.context.first_widget'; }
					else if (hasEnvelopePayload(ctx.envelope)) { envelope = ctx.envelope as Record<string, unknown>; source = 'event.context.envelope'; }
				}
				if (!slotState) {
					if (isRecord(ctx.final_slot_state)) slotState = ctx.final_slot_state as Record<string, unknown>;
					else if (isRecord(ctx.slot_state)) slotState = ctx.slot_state as Record<string, unknown>;
				}
				if (!botMessage) botMessage = String(ctx.bot_message || '').trim();
			}
			if (envelope && slotState && botMessage) break;
		}
	}

	// ── Tertiary: deep scan any object with widget_type + payload ──
	if (!envelope) {
		const seen = new Set<unknown>();
		const findEnvelopeDeep = (value: unknown, depth = 0): Record<string, unknown> | null => {
			if (depth > 7 || value === null || value === undefined || seen.has(value)) return null;
			if (typeof value === 'object') seen.add(value);
			if (hasEnvelopePayload(value)) return value as Record<string, unknown>;
			if (Array.isArray(value)) {
				for (let i = value.length - 1; i >= 0; i -= 1) {
					const found = findEnvelopeDeep(value[i], depth + 1);
					if (found) return found;
				}
				return null;
			}
			if (!isRecord(value)) return null;
			for (const candidate of Object.values(value)) {
				const found = findEnvelopeDeep(candidate, depth + 1);
				if (found) return found;
			}
			return null;
		};
		for (let i = events.length - 1; i >= 0; i -= 1) {
			envelope = findEnvelopeDeep(events[i]);
			if (envelope) { source = 'event_deep_scan'; break; }
		}
		if (!envelope) {
			envelope = findEnvelopeDeep(result) || findEnvelopeDeep(data);
			if (envelope) source = 'result_deep_scan';
		}
	}

	// Normalize synthetic place items using the extracted slotState so the widget
	// renders the actual destination instead of backend placeholder values.
	if (envelope && ['place_list', 'place_card'].includes(String(envelope.widget_type || ''))) {
		const regionLabel = isRecord(slotState?.region)
			? String((slotState!.region as Record<string, unknown>).label || '')
			: '';
		const fixItem = (item: unknown): unknown => {
			if (!isRecord(item)) return item;
			const name = String(item.name || regionLabel || '');
			const address = item.address === 'Suggested destination' ? name : item.address;
			let place_id = String(item.place_id || '');
			if (place_id.startsWith('synthetic-') && name) {
				place_id = `synthetic-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
			}
			return { ...item, name, address, place_id };
		};
		const p = isRecord(envelope.payload) ? (envelope.payload as Record<string, unknown>) : {};
		if (envelope.widget_type === 'place_list' && Array.isArray(p.items)) {
			envelope = { ...envelope, payload: { ...p, items: p.items.map(fixItem) } };
		} else if (envelope.widget_type === 'place_card') {
			envelope = { ...envelope, payload: fixItem(p) as Record<string, unknown> };
		}
	}

	return {
		envelope,
		slotState,
		botMessage,
		source: source || (envelope ? 'unknown' : 'not_found'),
	};
};

export const executeItineraryPlanner = async (
	token: string,
	turn: ItineraryTurnInput = {},
	options?: { signal?: AbortSignal; onExecutionId?: (id: string) => void }
): Promise<ItineraryPlaybookResult> => {
	const userMessage = turn.userMessage || '';
	const eventType = turn.eventType || 'user_message';
	// Always include `text` so the playbook's extract_turn step never receives
	// an empty text field, regardless of whether this is a user message or a
	// widget submission.
	const eventPayload = turn.eventPayload
		? { text: userMessage, ...turn.eventPayload }
		: { text: userMessage };
	const threadId = turn.threadId;
	const mutation = `
		mutation ExecutePlaybook($name: String!, $vars: JSON) {
			executePlaybook(name: $name, variables: $vars) {
				id
				executionId
				requestId
				name
				status
			}
		}
	`;

	const response = await fetch(`${resolveGatewayBaseUrl()}/graphql`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify({
			query: mutation,
			variables: {
				name: ITINERARY_PLAYBOOK_PATH,
				vars: {
					thread_id: threadId || `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
					event_type: eventType,
					event_payload: eventPayload,
					// Keep legacy key for compatibility while migrating callers.
					user_message: userMessage,
				},
			},
		}),
		signal: options?.signal,
	});

	if (response.status === 401) {
		throw new Error('Session expired. Please try again.');
	}
	if (!response.ok) {
		throw new Error(`Playbook request failed (${response.status})`);
	}

	const body = await response.json();
	if (body.errors?.length) {
		throw new Error(body.errors[0].message || 'GraphQL error');
	}

	const execution = body.data?.executePlaybook || {};
	const executionId = String(execution.executionId || execution.execution_id || execution.id || '').trim();
	if (!executionId) {
		throw new Error('Missing execution ID from playbook start response.');
	}

	options?.onExecutionId?.(executionId);
	const executionResult = await waitForExecutionResult(executionId, token, options?.signal);

	const snapshot = inspectExecutionResult(executionResult);
	const extracted = extractItineraryData(executionResult);

	if (process.env.NODE_ENV !== 'production') {
		const evts = Array.isArray((executionResult as any)?.events) ? (executionResult as any).events as Record<string, unknown>[] : [];
		const lastEvent = evts.length ? evts[evts.length - 1] : null;
		console.log('[itinerary-planner] extraction', {
			executionId,
			status: String((executionResult as Record<string, unknown>)?.status || ''),
			source: extracted.source,
			hasEnvelope: Boolean(extracted.envelope),
			envelopeWidgetType: String((extracted.envelope as any)?.widget_type || ''),
			hasSlotState: Boolean(extracted.slotState && Object.keys(extracted.slotState).length),
			hasBotMessage: Boolean((extracted.botMessage || '').trim()),
			eventsCount: evts.length,
			lastEventType: (lastEvent as any)?.event_type || null,
			lastEventNode: (lastEvent as any)?.node_name || null,
		});
	}

	return {
		executionId,
		status: String((executionResult as Record<string, unknown>)?.status || ''),
		startResponse: execution as Record<string, unknown>,
		snapshot,
		extracted,
		executionResult,
	};
};


/* ── muno/playbooks/flights-details (adiona/frontend#21) ─────────────────────
 *
 * Flight search returning EVERY offer Duffel had for the criteria — no
 * filtering, no ranking, no truncation beyond the playbook's 50-per-direction
 * cap. Same execute-then-poll shape as executeHotelCardsPlaybook above.
 */

export type FlightsDetailsOptions = {
	cabinClass?: 'economy' | 'premium_economy' | 'business' | 'first';
	/** Cap PER DIRECTION SET. 50 is the playbook maximum. */
	maxOffers?: number;
	/**
	 * Opt in to per-offer baggage and fare-condition OPTIONS.
	 *
	 * OFF by default and it should stay off for an interactive search: it costs
	 * the playbook one extra Duffel call per offer (~+6-7s per 10 offers). With
	 * it off, `offer.baggage` / `offer.conditions` carry only what the search
	 * response itself stated, which is usually nothing — see `baggage_known`.
	 */
	enrichOffers?: boolean;
	/**
	 * 'split' (default) runs a round trip as two independent searches, so each
	 * offer is priced and bookable on its own and carries a real `direction`.
	 * 'combined' runs one round-trip search and returns whole-trip offers
	 * tagged 'round-trip'. A combined fare is often cheaper than two one-ways.
	 */
	roundTripMode?: 'split' | 'combined';
};

export type FlightsDetailsPlaybookResult = {
	executionId: string;
	status: string;
	startResponse: Record<string, unknown>;
	offers: FlightOffer[];
	count: number;
	currency?: string;
	tripType: string;
	criteria: FlightSearchCriteria | null;
	source?: string;
	providerError?: unknown;
	/** e.g. { outbound: 37, inbound: 41 } on a split round trip. */
	offersByDirection: Record<string, number>;
	/** False means baggage/conditions arrays are UNKNOWN, not empty-because-none. */
	enriched: boolean;
	executionResult: Record<string, unknown>;
};

const hasOffersArray = (value: unknown): value is Record<string, unknown> & { offers: unknown[] } =>
	isRecord(value) && Array.isArray((value as Record<string, unknown>).offers);

const extractFlightsDetailsPayload = (
	executionResult: Record<string, unknown>
): Record<string, unknown> | null => {
	const result = isRecord(executionResult.result) ? (executionResult.result as Record<string, unknown>) : {};
	const data = isRecord(executionResult.data) ? (executionResult.data as Record<string, unknown>) : {};
	const events = Array.isArray(executionResult.events) ? (executionResult.events as Record<string, unknown>[]) : [];

	const directCandidates: unknown[] = [
		result,
		data,
		isRecord(result.result) ? result.result : null,
		isRecord(result.data) ? result.data : null,
		isRecord(data.result) ? data.result : null,
		isRecord(data.data) ? data.data : null,
	];

	for (const candidate of directCandidates) {
		if (hasOffersArray(candidate)) return candidate;
	}

	for (let i = events.length - 1; i >= 0; i -= 1) {
		const event = events[i];
		if (!isRecord(event)) continue;
		for (const node of [event.output_result, event.result]) {
			if (!isRecord(node)) continue;
			const context = isRecord((node as any).context)
				? ((node as any).context as Record<string, unknown>)
				: (node as Record<string, unknown>);
			if (hasOffersArray(context)) return context;
			if (hasOffersArray(context.result)) return context.result as Record<string, unknown>;
			if (hasOffersArray(context.data)) return context.data as Record<string, unknown>;
		}
	}

	// Final fallback: deep scan for the first object carrying offers[], to
	// tolerate runtime-specific wrapping (same tactic as the hotel path).
	const visited = new Set<unknown>();
	const findOffersDeep = (value: unknown, depth = 0): Record<string, unknown> | null => {
		if (depth > 9 || value === null || value === undefined || visited.has(value)) return null;
		if (typeof value === 'object') visited.add(value);
		if (hasOffersArray(value)) return value as Record<string, unknown>;
		if (Array.isArray(value)) {
			for (let i = value.length - 1; i >= 0; i -= 1) {
				const found = findOffersDeep(value[i], depth + 1);
				if (found) return found;
			}
			return null;
		}
		if (!isRecord(value)) return null;
		for (const nested of Object.values(value)) {
			const found = findOffersDeep(nested, depth + 1);
			if (found) return found;
		}
		return null;
	};

	return findOffersDeep(executionResult);
};

const str = (value: unknown, fallback = ''): string =>
	value === null || value === undefined ? fallback : String(value);

const normalizeAirline = (raw: unknown): FlightAirline | null => {
	if (!isRecord(raw)) return null;
	const iata = str(raw.iata_code);
	const name = str(raw.name);
	if (!iata && !name) return null;
	return {
		iata_code: iata,
		name,
		logo_symbol_url: str(raw.logo_symbol_url),
		logo_lockup_url: str(raw.logo_lockup_url),
	};
};

const normalizePlace = (raw: unknown): FlightPlace => {
	const place = isRecord(raw) ? raw : {};
	return {
		iata: str(place.iata || place.iataCode),
		at: str(place.at),
		city: str(place.city),
		airport_name: str(place.airport_name),
		terminal: str(place.terminal),
	};
};

const normalizeSegment = (raw: unknown): FlightSegment => {
	const seg = isRecord(raw) ? raw : {};
	const segment: FlightSegment = {
		departure: normalizePlace(seg.departure),
		arrival: normalizePlace(seg.arrival),
		carrier: str(seg.carrier || seg.carrierCode),
		carrier_name: str(seg.carrier_name),
		airline: normalizeAirline(seg.airline),
		flight_number: str(seg.flight_number || seg.number),
		duration: str(seg.duration, 'PT0H'),
		aircraft: isRecord(seg.aircraft)
			? { iata_code: str((seg.aircraft as Record<string, unknown>).iata_code), name: str((seg.aircraft as Record<string, unknown>).name) }
			: null,
		cabin_class: seg.cabin_class ? str(seg.cabin_class) : null,
		stops: Number(seg.stops || 0) || 0,
		technical_stops: Array.isArray(seg.technical_stops) ? seg.technical_stops : [],
	};
	// Codeshare fields only when the playbook actually emitted them.
	const operating = normalizeAirline(seg.operating_airline);
	if (operating) {
		segment.operating_carrier = str(seg.operating_carrier) || operating.iata_code;
		segment.operating_carrier_name = str(seg.operating_carrier_name) || operating.name;
		segment.operating_airline = operating;
		segment.operating_flight_number = str(seg.operating_flight_number);
	}
	return segment;
};

const normalizeItinerary = (raw: unknown, offerDirection: string): FlightItinerary => {
	const itin = isRecord(raw) ? raw : {};
	const segments = (Array.isArray(itin.segments) ? itin.segments : []).map(normalizeSegment);
	return {
		direction: str(itin.direction) || offerDirection,
		origin: normalizePlace(itin.origin),
		destination: normalizePlace(itin.destination),
		duration: str(itin.duration, 'PT0H'),
		stops: itin.stops === null || itin.stops === undefined ? Math.max(0, segments.length - 1) : Number(itin.stops) || 0,
		segments,
	};
};

const normalizeFlightOffer = (raw: Record<string, unknown>, index: number): FlightOffer => {
	const direction = str(raw.direction, 'one-way');
	const price = isRecord(raw.price) ? raw.price : {};
	const itineraries = (Array.isArray(raw.itineraries) ? raw.itineraries : []).map((item) =>
		normalizeItinerary(item, direction)
	);
	const airlines = (Array.isArray(raw.airlines) ? raw.airlines : [])
		.map(normalizeAirline)
		.filter((airline): airline is FlightAirline => airline !== null);

	return {
		offer_id: str(raw.offer_id || raw.id, `offer-${index + 1}`),
		direction,
		price: {
			total: str(price.total, '0'),
			currency: str(price.currency, 'USD'),
			base: price.base === null || price.base === undefined ? null : str(price.base),
			tax: price.tax === null || price.tax === undefined ? null : str(price.tax),
		},
		itineraries,
		carriers: (Array.isArray(raw.carriers) ? raw.carriers : []).map((code) => str(code)).filter(Boolean),
		airline: normalizeAirline(raw.airline) || airlines[0] || null,
		airlines,
		duration: str(raw.duration, 'PT0H'),
		stops: Number(raw.stops || 0) || 0,
		validating_airline: str(raw.validating_airline),
		// Pass the option arrays through as-is: they are already the wire shape,
		// and an empty array means UNKNOWN. Substituting a default here would
		// turn "the provider did not say" into "there is no bag".
		baggage_options: (Array.isArray(raw.baggage_options) ? raw.baggage_options : []) as FlightBaggageOption[],
		baggage_known: Boolean(raw.baggage_known),
		conditions_options: (Array.isArray(raw.conditions_options) ? raw.conditions_options : []) as FlightConditionOption[],
		conditions_known: Boolean(raw.conditions_known),
		// Collapsed dicts, byte-compatible with the planner's ranker. null means
		// the provider carried no verdict at all.
		baggage: isRecord(raw.baggage) ? (raw.baggage as unknown as FlightBaggageSummary) : null,
		conditions: isRecord(raw.conditions) ? (raw.conditions as unknown as FlightConditionsSummary) : null,
		fare_brand: raw.fare_brand === null || raw.fare_brand === undefined ? null : str(raw.fare_brand),
		passenger_count: raw.passenger_count === null || raw.passenger_count === undefined ? null : Number(raw.passenger_count),
		expires_at: raw.expires_at ? str(raw.expires_at) : null,
		live_mode: raw.live_mode === null || raw.live_mode === undefined ? null : Boolean(raw.live_mode),
		ctas: (Array.isArray(raw.ctas) ? raw.ctas : ['view_details', 'book_this']).map((cta) => str(cta)),
	};
};

export const executeFlightsDetailsPlaybook = async (
	token: string,
	criteria: FlightSearchCriteria,
	options?: FlightsDetailsOptions & { signal?: AbortSignal; onExecutionId?: (id: string) => void }
): Promise<FlightsDetailsPlaybookResult> => {
	const origin = String(criteria?.origin || '').trim().toUpperCase();
	const destination = String(criteria?.destination || '').trim().toUpperCase();
	const departDate = String(criteria?.departDate || '').trim();
	if (!origin || !destination || !departDate) {
		throw new Error('Flight search needs an origin, a destination and a departure date.');
	}
	const tripType = criteria?.tripType === 'round_trip' ? 'round_trip' : 'one_way';
	const returnDate = tripType === 'round_trip' ? String(criteria?.returnDate || '').trim() : '';
	if (tripType === 'round_trip' && !returnDate) {
		throw new Error('A round trip needs a return date.');
	}
	// Children are REAL aged passengers: Duffel prices a child fare from the
	// age, so a bare count is rejected by the playbook rather than silently
	// charged as an adult. Surface that here instead of after a round trip to
	// the gateway.
	const children = (criteria?.travelers?.children || []).map((child) => {
		const age = Number((child as any)?.age);
		if (!Number.isInteger(age) || age < 0 || age > 17) {
			throw new Error('Each child traveler needs a whole age between 0 and 17.');
		}
		return { age };
	});
	const adults = Number(criteria?.travelers?.adults ?? 1) || 1;

	const mutation = `
		mutation ExecutePlaybook($name: String!, $vars: JSON) {
			executePlaybook(name: $name, variables: $vars) {
				id
				executionId
				requestId
				name
				status
			}
		}
	`;

	// Send a full workload payload to avoid backend default-merging differences
	// (same reason as the hotel-cards path).
	const variables = {
		trip_type: tripType,
		origin,
		destination,
		depart_date: departDate,
		return_date: returnDate,
		adults,
		children,
		cabin_class: options?.cabinClass || 'economy',
		max_offers: options?.maxOffers ?? 50,
		enrich_offers: options?.enrichOffers ?? false,
		round_trip_mode: options?.roundTripMode || 'split',
		criteria: {
			tripType,
			origin,
			destination,
			departDate,
			returnDate: returnDate || undefined,
			travelers: { adults, children },
		},
	};

	const response = await fetch(`${resolveGatewayBaseUrl()}/graphql`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify({
			query: mutation,
			variables: {
				name: FLIGHTS_DETAILS_PLAYBOOK_PATH,
				vars: variables,
			},
		}),
		signal: options?.signal,
	});

	if (response.status === 401) {
		throw new Error('Session expired. Please try again.');
	}
	if (!response.ok) {
		throw new Error(`Flight search request failed (${response.status})`);
	}

	const body = await response.json();
	if (body.errors?.length) {
		throw new Error(body.errors[0].message || 'GraphQL error');
	}

	const execution = body.data?.executePlaybook || {};
	const executionId = String(execution.executionId || execution.execution_id || execution.id || '').trim();
	if (!executionId) {
		throw new Error('Missing execution ID from flights-details playbook response.');
	}

	options?.onExecutionId?.(executionId);
	const executionResult = await waitForExecutionResult(executionId, token, options?.signal);
	const payload = extractFlightsDetailsPayload(executionResult) || {};
	const rawOffers = Array.isArray(payload.offers) ? (payload.offers as Record<string, unknown>[]) : [];
	const offers = rawOffers.map((offer, index) => normalizeFlightOffer(offer, index));

	if (process.env.NODE_ENV !== 'production') {
		console.log('[flights-details] extraction', {
			executionId,
			status: String((executionResult as Record<string, unknown>)?.status || ''),
			offersCount: offers.length,
			offersByDirection: payload.offers_by_direction || null,
			enriched: Boolean(payload.enriched),
			payloadKeys: Object.keys(payload),
			providerError: payload.provider_error || null,
			requestVariables: variables,
		});
	}

	return {
		executionId,
		status: String((executionResult as Record<string, unknown>)?.status || ''),
		startResponse: execution as Record<string, unknown>,
		offers,
		count: Number(payload.count ?? offers.length) || offers.length,
		currency: payload.currency ? String(payload.currency) : undefined,
		tripType: String(payload.trip_type || tripType),
		criteria: isRecord(payload.criteria) ? (payload.criteria as unknown as FlightSearchCriteria) : null,
		source: payload.source ? String(payload.source) : undefined,
		providerError: payload.provider_error ?? null,
		offersByDirection: isRecord(payload.offers_by_direction)
			? (payload.offers_by_direction as Record<string, number>)
			: {},
		enriched: Boolean(payload.enriched),
		executionResult,
	};
};
